"use client";

import {CoolCard} from "@/components/common/CoolCard";
import {MobiTitle} from "@/components/common/MobiTitle";
import {
    Button,
    Card,
    Center, Divider,
    Group, SegmentedControl, Stack, Text, useMantineColorScheme,
    useMantineTheme
} from "@mantine/core";
import {Span} from "@/components/common/Span";
import {
    useHighContrastColor, usePrimaryColor,
    useSlightlyColoredText,
    useSlightlyDimmedColor
} from "@/components/common/hooks/useColors";
import {Para} from "@/components/common/Para";
import {PricingPlan} from "@/components/pricing/PricingPlan";
import {BillingCycle, PlanKinds} from "@/app/lib/pricing/types";
import {CSSProperties, useCallback, useMemo, useState} from "react";
import {PricingDetails} from "@/components/pricing/use-pricing";
import {Accent} from "@/components/common/Accent";
import {pricingStat} from "@/app/lib/pricing/utils";
import {Emp} from "@/components/common/Emp";
import {formattedPrice} from "@/components/pricing/price-utils";

export type PricingPlanWidgetProps = {
    subscriptionError: boolean;
    plan: PricingPlan;
    planPlus: PricingPlan;
    billing: BillingCycle;
    pricingDetails: PricingDetails;
    loadingPrices: boolean;
    loadingPaymentSystem: boolean;
    paymentSystemReady: boolean;
    onCheckout: (plan: PricingPlan, billing: BillingCycle) => void;
    onSignupWithPlan: (plan: PricingPlan, billing: BillingCycle) => void;
};

export function PricingPlanWidget(
    {
        subscriptionError,
        plan,
        planPlus,
        billing,
        pricingDetails,
        loadingPrices,
        onCheckout,
        onSignupWithPlan,
        loadingPaymentSystem,
        paymentSystemReady,
    }: PricingPlanWidgetProps
) {
    const theme = useMantineTheme();
    const {colorScheme: scheme} = useMantineColorScheme();

    const high = useHighContrastColor();
    const priceSize = 30;
    const priceFractionSize = 16;
    const discountedPriceSize = 24;
    const summarySize = 20;
    const statValueSize = 22;
    const statLabelSize = 14;
    const primaryColor = usePrimaryColor();
    const dimmed = useSlightlyDimmedColor();
    const slightlyColored = useSlightlyColoredText();

    const monthSuffix = 'month';
    const yearSuffix = 'year';

    const [planKind, setPlanKind] = useState<string>(PlanKinds.acoustic);
    const selectedPlan = planKind === PlanKinds.acoustic ? plan : planPlus;

    const {
        name,
        stats,
        features,
        disabled,
        audience,
        monthlyPrice,
        annualPrice
    } = selectedPlan;

    const discount = pricingDetails.feedbackDiscount + pricingDetails.affiliateDiscount;

    const price = billing === 'monthly' ? monthlyPrice : annualPrice;
    const fullAnnualPrice = monthlyPrice.amount * 12;

    const period = billing === 'monthly' ? monthSuffix : yearSuffix;
    const periodAdj = billing === 'monthly' ? 'monthly' : 'annually';
    // const priceColor = billing === 'monthly' ?
    //     high :
    //     theme.colors[theme.primaryColor][scheme === 'light' ? 6 : 6];
    const priceColor = high;

    const orgPriceText = formattedPrice(price.amount);
    const orgPriceParts = orgPriceText.split('.');
    const orgPriceWhole = orgPriceParts[0];
    const orgPriceFraction = orgPriceParts[1];

    const annualPriceText = fullAnnualPrice.toFixed(2);

    const discountedPrice = discount > 0 ? price.amount * (1 - discount / 100) : price.amount;
    const priceText = formattedPrice(discountedPrice);
    const priceParts = priceText.split('.');
    const priceWhole = priceParts[0];
    const priceFraction = priceParts[1];

    const discountAmount = discount > 0 ? price.amount * (discount / 100) : 0;
    const discountText = formattedPrice(discountAmount);

    const bgColor = scheme === 'dark' ? theme.black : theme.white;

    const sectionBackground = useMemo(() => {
        return {
            backgroundImage: scheme === 'dark' ?
                "url('/images/backdrop-dark.png')" :
                "url('/images/backdrop-light.png')",
            backgroundSize: 'cover'
        } as CSSProperties;
    }, [scheme]);

    const loggedIn = pricingDetails.loggedIn ?? false;
    const alreadySubscribed = loggedIn && pricingDetails.existingPlan != null;
    const canSubscribeNewPlan = !loggedIn || !alreadySubscribed;
    const foreverFree = pricingDetails.profile?.isFree ?? false;

    const isLoading = pricingDetails.loadingProfile || pricingDetails.apiCallInProgress;

    const isDisabled = disabled ||
        foreverFree ||
        subscriptionError ||
        !paymentSystemReady ||
        !canSubscribeNewPlan;

    const onSubscribe = useCallback(() => {
        if (isDisabled) {
            return;
        }
        if (loggedIn) {
            onCheckout(selectedPlan, billing);
        } else {
            onSignupWithPlan(selectedPlan, billing);
        }
    }, [billing, isDisabled, loggedIn, onCheckout, onSignupWithPlan, selectedPlan]);

    return (
        <CoolCard radius={'md'} w={'100%'} h={'100%'} shadow={'xl'}>
            <Card.Section bg={bgColor} withBorder>
                <Stack gap={'xs'} py={8} px={'md'}>
                    <Stack gap={4}>
                        <MobiTitle order={2}
                                   style={{whiteSpace: 'nowrap', userSelect: 'none'}}
                                   ta={'center'}
                                   c={slightlyColored}>{name}</MobiTitle>
                        <Para fz={summarySize} c={dimmed} ta={'center'}>{audience}</Para>
                    </Stack>
                    <SegmentedControl fullWidth
                        // color={theme.primaryColor}
                                      value={planKind}
                                      onChange={setPlanKind}
                                      data={[
                                          {
                                              value: 'acoustic',
                                              label:
                                                  <Stack gap={0}>
                                                      <Span style={{whiteSpace: 'nowrap'}}
                                                            fw={planKind === 'acoustic' ? 700 : 600}>
                                                          Without feedback</Span>
                                                      <Span c={dimmed} fz={'xs'}
                                                            fw={planKind === 'acoustic' ? 600 : 500}>Acoustic
                                                          drums</Span>
                                                  </Stack>
                                          },
                                          {
                                              value: 'electronic',
                                              label:
                                                  <Stack gap={0}>
                                                      <Span style={{whiteSpace: 'nowrap'}}
                                                            fw={planKind === 'electronic' ? 700 : 600}>
                                                          With <Span c={theme.primaryColor}>
                                                          Live feedback</Span></Span>
                                                      <Span c={dimmed} fz={'xs'}
                                                            fw={planKind === 'electronic' ? 600 : 500}>Electronic
                                                          drums <Span c={theme.primaryColor}>**</Span></Span>
                                                  </Stack>
                                          }
                                      ]}/>
                </Stack>
            </Card.Section>
            <Stack gap={'sm'} justify={'stretch'} px={'md'}>
                <Stack gap={'xs'} py={'sm'}>
                    {/*<Para fz={summarySize} c={dimmed} ta={'center'}>{audience}</Para>*/}
                    <Stack gap={0} w={'100%'} align={'center'}>
                        {discount > 0 && (
                            <Stack gap={4} align={'center'}>
                                <Group gap={'xs'} align={'center'} wrap={'nowrap'}>
                                    <Span c={'dimmed'} fw={700} fz={discountedPriceSize}
                                          ff={'var(--font-montserrat)'}
                                          style={{
                                              textDecorationLine: 'line-through',
                                              textDecorationColor: primaryColor,
                                          }}
                                    >{orgPriceText}</Span>
                                    <Span c={'dimmed'}> {price.currency}/{period}</Span>
                                </Group>
                            </Stack>
                        )}
                        {discount === 0 && billing === 'yearly' && (
                            <Stack gap={4} align={'center'}>
                                <Group gap={'xs'} align={'center'} wrap={'nowrap'}>
                                    <Span c={'dimmed'} fw={700} fz={discountedPriceSize}
                                          ff={'var(--font-montserrat)'}
                                          style={{
                                              textDecorationLine: 'line-through',
                                              textDecorationColor: primaryColor,
                                          }}>{annualPriceText}</Span>
                                    <Span c={'dimmed'}> {price.currency}/{period}</Span>
                                </Group>
                            </Stack>
                        )}
                        <Group gap={'xs'} align={'center'} wrap={'nowrap'}>
                            <Span>
                                <Span c={priceColor} fw={700} fz={priceSize}
                                      ff={'var(--font-montserrat)'}>{priceWhole}.</Span>
                                <Span c={priceColor} fw={700} fz={priceFractionSize}
                                      ff={'var(--font-montserrat)'}
                                      style={{
                                          verticalAlign: 'super',
                                          position: "relative",
                                          top: -3
                                      }}>{priceFraction}</Span>
                            </Span>
                            <Span><Span fw={'bold'}>{price.currency}</Span>/{period}</Span>
                        </Group>
                        {discount > 0 && (
                            <Stack gap={4} align={'center'}>
                                <Span fz={'md'} ta={'center'}><Span>Enjoy <Emp
                                    c={theme.primaryColor}>{pricingDetails.feedbackDiscount + pricingDetails.affiliateDiscount}%
                                    {' '}</Emp> discount<Emp c={theme.primaryColor}>*</Emp>
                                    {' '}for the 1st {period}
                                </Span>, then {' '}{orgPriceWhole}.<sup><small>{orgPriceFraction}</small></sup>/{period}.
                                </Span>
                            </Stack>
                        )}
                        <Span fz={'md'} ta={'center'}
                              c={dimmed}>Billed <Span fw={600}>{periodAdj}</Span>{billing === 'yearly' ?
                            <Span>, <Emp c={theme.primaryColor}>saving
                                {' '}{formattedPrice(Math.trunc((fullAnnualPrice - annualPrice.amount)))}+</Emp>/year</Span> :
                            <Span>. Switch to yearly billing to <Emp>save
                                {' '}{formattedPrice(Math.trunc((fullAnnualPrice - annualPrice.amount)))}+</Emp>/year</Span>
                        }.
                        </Span>
                    </Stack>
                    {discount > 0 && (
                        <Stack gap={'xs'} py={'sm'}>
                            <Divider/>
                            <Span fz={'xs'} ta={'center'}>
                                <Emp c={theme.primaryColor}>* </Emp>
                                {pricingDetails.feedbackDiscount > 0 && pricingDetails.affiliateDiscount > 0 && (
                                    <Span>
                                        Includes <Accent>one-off</Accent>
                                        {' '}<Emp>{pricingDetails.feedbackDiscount}%</Emp> discount for the feedback you
                                        {' '}provided, plus <Accent>one-off</Accent>
                                        {' '}<Emp>{pricingDetails.affiliateDiscount}%</Emp> affiliate discount.
                                    </Span>
                                )}
                                {pricingDetails.feedbackDiscount > 0 && pricingDetails.affiliateDiscount === 0 && (
                                    <Span>
                                        <Accent>One-off</Accent> <Emp>{pricingDetails.feedbackDiscount}%</Emp> discount
                                        {' '}for the feedback you provided.
                                    </Span>
                                )}
                                {pricingDetails.feedbackDiscount === 0 && pricingDetails.affiliateDiscount > 0 && (
                                    <Span>
                                        <Accent>One-off</Accent>
                                        {' '}<Emp>{pricingDetails.affiliateDiscount}%</Emp> affiliate discount.
                                    </Span>
                                )}
                            </Span>
                        </Stack>
                    )}
                </Stack>
            </Stack>
            <Card.Section withBorder style={sectionBackground}>
                <Stack py={'xs'}>
                    <Center>
                        <Button px={'xl'}
                            // disabled={isDisabled} -> handled by onSubscribe
                                loading={isLoading}
                                loaderProps={{type: 'dots'}}
                                variant={isDisabled ? 'default' : 'filled'}
                                c={isDisabled ? 'dimmed' : undefined}
                                style={{pointerEvents: isDisabled ? 'none' : undefined}}
                                size={'lg'}
                                onClick={onSubscribe}
                        >
                            {disabled ?
                                'Coming soon' :
                                <Stack gap={0} my={'md'} align={'center'}>
                                    <Text inherit>Get it</Text>
                                    <Text fz={'xs'} fw={'normal'}>and free your limbs</Text>
                                </Stack>
                            }
                        </Button>
                    </Center>
                </Stack>
            </Card.Section>
            <Stack gap={'md'} my={'sm'} justify={'stretch'} h={'100%'}>
                {features}
            </Stack>
            {stats != null && (
                <Card.Section withBorder style={sectionBackground}>
                    <Stack py={6} px={'lg'}>
                        <Group justify={'space-between'} wrap={'nowrap'}>
                            {stats.ostinatos > 0 && (
                                <Stack gap={0} justify={'center'} align={'center'}>
                                    <Span c={slightlyColored} ff={'var(--font-montserrat)'} fz={statValueSize}
                                          fw={'bold'}>{pricingStat(stats.ostinatos)}</Span>
                                    <Span tt={'uppercase'} c={'dimmed'} fz={statLabelSize}
                                          ff={'var(--font-play)'}>Ostinatos</Span>
                                </Stack>
                            )}
                            <Stack gap={0} justify={'center'} align={'center'}>
                                <Span c={slightlyColored} ff={'var(--font-montserrat)'} fz={statValueSize}
                                      fw={'bold'}>{pricingStat(stats.exercises)}</Span>
                                <Span tt={'uppercase'} c={'dimmed'} fz={statLabelSize}
                                      ff={'var(--font-play)'}>Exercises</Span>
                            </Stack>
                            <Stack gap={0} justify={'center'} align={'center'}>
                                <Span c={slightlyColored} ff={'var(--font-montserrat)'} fz={statValueSize}
                                      fw={'bold'}>
                                    {/*
                                    <Tooltip label={'Infinite variations'}>
                                        <IconInfinity/>
                                    </Tooltip>
*/}
                                    Infinite
                                    {/*{pricingStat(stats.variations)}*/}
                                </Span>
                                <Span tt={'uppercase'} c={'dimmed'} fz={statLabelSize}
                                      ff={'var(--font-play)'}>Variations</Span>
                            </Stack>
                            <Stack gap={0} justify={'center'} align={'center'}>
                                <Span c={slightlyColored} ff={'var(--font-montserrat)'} fz={statValueSize}
                                      fw={'bold'}>{pricingStat(stats.tracks)}</Span>
                                <Span tt={'uppercase'} c={'dimmed'} fz={statLabelSize}
                                      ff={'var(--font-play)'}>Tracks</Span>
                            </Stack>
                        </Group>
                    </Stack>
                </Card.Section>
            )}
        </CoolCard>
    )
}
